import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Property from '@afosto/app-components-react/Property';
import { payments as paymentsApi } from '@afosto/order/api/Payments';
import translations from './translations';

const PaymentDetailsSection = ({ paymentMethod }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:providers:read']);
  const [paymentMethodData, setPaymentMethodData] = useState(null);
  const [isFetchingPaymentMethod, setIsFetchingPaymentMethod] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const { method_code: methodCode, issuer_code: issuerCode, provider_code: provideCode } =
      paymentMethod || {};

    const fetchPaymentMethod = async () => {
      setIsFetchingPaymentMethod(true);
      try {
        const { data: method } = await paymentsApi.getPaymentMethod(provideCode, methodCode);
        const issuer = method?.issuers?.find(issuerItem => issuerItem?.code === issuerCode);

        if (isMounted) {
          setPaymentMethodData({
            method: { ...method, label: method?.name, value: method?.code },
            issuer: { ...issuer, label: issuer?.name, value: issuer?.code },
            provider: provideCode,
          });
        }
      } catch (err) {
        if (isMounted) {
          setPaymentMethodData(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPaymentMethod(false);
        }
      }
    };

    if (methodCode && provideCode) {
      fetchPaymentMethod();
    }

    return () => {
      isMounted = false;
    };
  }, [paymentMethod]);

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <LoadingWrapper isLoading={isFetchingPaymentMethod}>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <Property
            label={intl.formatMessage(translations.overview.paymentMethod)}
            value={
              paymentMethodData?.method?.code
                ? intl.formatMessage({
                    id: `paymentMethods.${paymentMethodData?.provider?.toLowerCase()}.${
                      paymentMethodData?.method?.code
                    }`,
                    defaultMessage: paymentMethodData?.method?.name,
                    description: `label of the payment method for code ${paymentMethodData?.method?.code}`,
                  })
                : null
            }
          />
          {paymentMethodData?.issuer?.code && (
            <Property
              label={intl.formatMessage(translations.overview.paymentIssuer)}
              value={paymentMethodData?.issuer?.name}
            />
          )}
          <Property
            label={intl.formatMessage(translations.overview.paymentProvider)}
            value={
              paymentMethodData?.provider
                ? intl.formatMessage({
                    id: `paymentMethods.providers.${paymentMethodData?.provider?.toLowerCase()}`,
                    defaultMessage: paymentMethodData?.provider,
                    description: `label of the payment provider ${paymentMethodData?.provider}`,
                  })
                : null
            }
          />
        </CardContent>
      </LoadingWrapper>
    </Card>
  );
};

PaymentDetailsSection.propTypes = {
  paymentMethod: PropTypes.object,
};

PaymentDetailsSection.defaultProps = {
  paymentMethod: {},
};

export default PaymentDetailsSection;
